import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TemplateNavigationPath, TemplatesFilter, TemplatesSummary } from "../models/templates";
import { getTomorrowAt9, getTomorrowNlFormatted } from "../utils/testDates";

interface Templates {
  selectedPath?: TemplateNavigationPath | null;
  selectedVersion: string;
  selectedLanguage: string;
  selectedTemplateSummary: TemplatesSummary | null;
  filter: TemplatesFilter;
  favorites: string[];
  showOnlyLiveTranslations: boolean;
  templateExtendsOtherTemplate: boolean;
  renderingVariables: string;
}

const initialRenderingVariables = `{
  "EMAILS_TO": ["test@colibry.ai"],
  "EMAILS_BCC": [""],

  "DATE": "${getTomorrowAt9()}",
  "BZ_DATUM": "${getTomorrowNlFormatted()}",
  "BZ_TIJDSTIP_AFSPRAAK": "15:30",
  "FIRSTNAME": "Vik",
  "LASTNAME": "Visitor",
  "AMOUNT_OF_MATCHES": 1,
  "WEBSITE_URL": "https://colibry.ai",

  "FROM_NAME": "Responsible Rep",
  "EMAIL_FROM": "wendy@colibry.ai",

  "PA_DOSSIERVERANTWD": "Responsible Rep",
  "PA_IMAGE": "https://storage.googleapis.com/production-co-libry-sp-js/a8ufNDWE/imaginary_forsale.jpg",
  "PA_LINK": "http://some-link-to-property",
  "PA_ADRES": "Pandadres 1, 9000, Gent",
  "PA_ADRES_ALWAYS_PROPERTY": "Pandadres 1, 9000, Gent",
  "PA_TYPE": "het appartement",
  "PA_GOOGLE_MAPS": "https://www.google.com/maps?q=Kompasplein+19/1A+Gent",
  "PA_ID": "TEST_ID",

  "RA_IMAGE": "https://storage.googleapis.com/automation-cdn/colibry/rexy-realtor-signature-example-me.jpg",
  "RA_FIRSTNAME": "Wendy",
  "RA_LASTNAME": "Geeraert",
  "RA_EMAIL": "wendy@colibry.ai",
  "RA_FUNCTION": "Colibry Founder as Rep.",
  "RA_MOBILE": "0477/77.77.77",
  "RA_BIV": "BIV number",

  "OI_IMAGE": "https://storage.googleapis.com/production-co-libry-sp-js/a8ufNDWE/imaginary_office.jpg",
  "OI_NAME": "9000 Gent",
  "OI_ADDRESS": "Office Address 1",
  "OI_PHONE": "09/123.44.55",

  "AMOUNT_OF_TASKS": 1,

  "HIGHLIGHT_COLOR": "#182b49"
}`;

const initialState: Templates = {
  selectedPath: null,
  selectedVersion: "draft",
  selectedLanguage: "nl",
  selectedTemplateSummary: null,
  filter: {
    hasDraftVersion: false,
    hasLiveVersion: false,
  },
  favorites: [],
  showOnlyLiveTranslations: false,
  templateExtendsOtherTemplate: false,
  renderingVariables: initialRenderingVariables,
};

const templatesSlice = createSlice({
  name: "templates",
  initialState: { ...initialState },
  reducers: {
    setSelectedPath(state, action: PayloadAction<TemplateNavigationPath>) {
      state.selectedPath = action.payload;
    },
    setSelectedVersion(state, action: PayloadAction<string>) {
      state.selectedVersion = action.payload;
    },
    setSelectedLanguage(state, action: PayloadAction<string>) {
      state.selectedLanguage = action.payload;
    },
    setSelectedTemplateSummary(state, action: PayloadAction<TemplatesSummary | null>) {
      state.selectedTemplateSummary = action.payload;
    },
    setFilter(state, action: PayloadAction<TemplatesFilter>) {
      state.filter = action.payload;
    },
    clearFilter(state) {
      state.filter = { ...initialState.filter };
    },
    addFavorite(state, action: PayloadAction<string>) {
      state.favorites = [...state.favorites, action.payload].sort();
    },
    removeFavorite(state, action: PayloadAction<string>) {
      state.favorites = state.favorites.filter((fav) => fav !== action.payload);
    },
    setShowOnlyLiveTranslations(state, action: PayloadAction<boolean>) {
      state.showOnlyLiveTranslations = action.payload;
    },
    setTemplateExtendsOtherTemplate(state, action: PayloadAction<boolean>) {
      state.templateExtendsOtherTemplate = action.payload;
    },
    setRenderingVariables(state, action: PayloadAction<string>) {
      state.renderingVariables = action.payload;
    },
  },
});

export const templatesActions = templatesSlice.actions;

export default templatesSlice;
